import styled from '@emotion/styled';
import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { Typography } from 'antd';
import Image from 'next/image';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

const CaseStudyCardFlex = styled(Flex)`
  padding: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid ${Colors.grayOpacity07};
  cursor: pointer;
  &:hover {
    border-color: ${Colors.grayOpacity12};
    box-shadow: 0px 8px 16px 0px rgba(29, 34, 37, 0.02);
  }
`;

const CaseStudyCard = ({
  url,
  logoSrc,
  key,
  logoWidth,
  logoHeight,
  description,
  author,
}: {
  url: string;
  logoSrc: string;
  key: string;
  logoWidth: number;
  logoHeight: number;
  description: string;
  author: string;
}) => {
  return (
    <CaseStudyCardFlex
      gap={4}
      direction="column"
      align="flex-start"
      onClick={() => {
        window.open(url, '_blank');
        mixpanel.track(MIXPANEL_EVENT.HOME.CLICK_FINANCE_USECASE, {
          resourceKey: key,
        });
      }}
    >
      <Image src={logoSrc} alt={key} width={logoWidth} height={logoHeight} />
      <Flex direction="column" style={{ height: 4 }} />
      <Typography.Text style={{ ...typographyStyle['16_600'], color: Colors.gray800 }}>
        {description}
      </Typography.Text>
      <Typography.Text style={{ ...typographyStyle['14_500'], color: Colors.gray800 }}>
        {author}
      </Typography.Text>
    </CaseStudyCardFlex>
  );
};

interface UsecaseSectionProps {
  containerRef: React.RefObject<HTMLDivElement>;
}
const UsecaseSection = ({ containerRef }: UsecaseSectionProps) => {
  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <Flex
        direction="column"
        gap={16}
        style={{
          alignItems: 'flex-start',
          alignSelf: 'stretch',
          position: 'relative',
          width: 380,
          paddingBottom: 170,
        }}
      >
        <Flex
          direction="row"
          justify="space-between"
          align="center"
          style={{
            alignSelf: 'stretch',
          }}
        >
          <Typography.Text style={{ ...typographyStyle['16_600'], color: Colors.gray800 }}>
            클로브 금융을 활용중인 회사들
          </Typography.Text>
        </Flex>
        <CaseStudyCard
          url="https://clobe.ai/customer-success/athome"
          logoSrc="https://static.clobe.ai/image/ci_athome_x2.png"
          key="athome"
          logoWidth={94}
          logoHeight={22}
          description="쓰면 쓸수록 이득이라 반복해서 활용했는데 돌이켜보니 어느새 6번이나 되어서 놀랐습니다."
          author="생활밀착형 브랜드 운영사 앳홈 손현욱 COO"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/deepdive"
          logoSrc="https://static.clobe.ai/image/ci_verish.png"
          key="verish"
          logoWidth={80}
          logoHeight={19}
          description="클로브 성장자금은 연 매출 300억 달성 궤도로 가는 마중물을 열어주었어요."
          author="여성 언더웨어 브랜드 베리시 운영사 딥다이브 이성은 Founder & CEO"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/nuldam"
          logoSrc="https://static.clobe.ai/image/ci_nuldam_x2.png"
          key="reebonz"
          logoWidth={81}
          logoHeight={17}
          description="클로브 금융은 필요할 때, 필요한 만큼 쓸 수 있다는 게 큰 장점이라고 느꼈습니다."
          author="비건 디저트 브랜드 운영사 조인앤조인 진해수 대표이사"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/bat"
          logoSrc="https://static.clobe.ai/image/ci_bat.png"
          key="bat"
          logoWidth={31}
          logoHeight={31}
          description="미래의 매출을 현금화해 먼저 투입해야 하는 광고 매체비를 조달하고 있어요."
          author="브랜드 에이전시 BAT 전상면 CFO & CSO"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/bomsen"
          logoSrc="https://static.clobe.ai/image/ci_bomsen_x2.png"
          key="bomsen"
          logoWidth={96}
          logoHeight={25}
          description="높은 ROAS를 기록하는 캠페인에 자금을 추가로 투입해 장기 계약까지 추가로 수주할 수 있었어요."
          author="종합광고대행사 봄센 오덕 대표이사"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/lemouton"
          logoSrc="https://static.clobe.ai/image/ci_lemouton.png"
          key="lemouton"
          logoWidth={124}
          logoHeight={20}
          description="클로브 금융으로 성수기에 제조∙운영자금을 늘리고 매출 또한 크게 성장했어요."
          author="르무통 운영사 우주텍 이명기 CFO"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/reebonz"
          logoSrc="https://static.clobe.ai/image/ci_reebonz.png"
          key="reebonz"
          logoWidth={108}
          logoHeight={14}
          description="기존 렌털 제품의 미래 매출을 현금화하고, 신규 렌털 자산을 확보하고 있어요."
          author="명품 렌탈 플랫폼 렌트잇 운영사 리본즈 김동환 CFO"
        />

        <CaseStudyCard
          url="https://clobe.ai/customer-success/planz"
          logoSrc="https://static.clobe.ai/image/ci_planz.png"
          key="planz"
          logoWidth={34}
          logoHeight={34}
          description="클로브에서 커피 스테이션 생산 자금 조달하고, 가장 중요한 설비 확장 문제를 해결했어요."
          author="무인 커피 스테이션 플랜즈커피 최준혁 Founder & CEO"
        />
        <CaseStudyCard
          url="https://clobe.ai/customer-success/youniv"
          logoSrc="https://static.clobe.ai/image/ci_youniv.png"
          key="youniv"
          logoWidth={86}
          logoHeight={16}
          description="클로브로 성수기에 추가 운영자금을 확보하고 중요한 시기를 잘 보냈어요."
          author="연고티비로 시작한 에듀테크 기업 유니브 신인수 경영기획팀장"
        />
      </Flex>
    </div>
  );
};

export default UsecaseSection;
